import { DeviceOperationalState } from '@/__generated__/graphql';
import { getDeviceOperationalStateAttributes } from '@/utils/attributes/deviceOperationalState';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { VisibilityOff } from '@mui/icons-material';
import { Box, Chip, Tooltip } from '@mui/material';
import { type FC, type ReactNode } from 'react';

interface Props {
  operationalState?: DeviceOperationalState | null;
  /** AMARU-606: flag manual del device. `false` → oculto para el cliente independientemente del estado. */
  visibleToClient?: boolean | null;
  /**
   * AMARU-606: ¿el operationalState por sí solo (ignorando `visibleToClient`) es visible para
   * el cliente? Viene resuelto por el backend (no duplicar CLIENT_OPERATIONAL_STATES acá).
   * Si se omite, se asume `true` (no contribuye como causa) — back-compat con callers viejos.
   */
  operationalStateVisibleToClient?: boolean | null;
}

/**
 * Marca un acceso concedido que el cliente aún NO ve. Puede haber hasta 2 causas ortogonales
 * simultáneas, y el tooltip debe listarlas TODAS (no priorizar una sobre otra):
 * - AMARU-597: estado operacional fuera de CLIENT_OPERATIONAL_STATES.
 * - AMARU-606: flag manual `visibleToClient=false` en el device.
 * Solo aparece en la vista de admin (Capta): para un caller cliente la lista de accesos ya
 * viene filtrada, así que estos registros no se muestran.
 */
const ClientHiddenChip: FC<Props> = ({
  operationalState,
  visibleToClient,
  operationalStateVisibleToClient,
}) => {
  const { _, i18n } = useLingui();
  const stateLabel = operationalState
    ? getDeviceOperationalStateAttributes(operationalState, i18n).label
    : null;
  const hiddenManually = visibleToClient === false;
  const hiddenByState = operationalStateVisibleToClient === false;

  const reasons: ReactNode[] = [];
  if (hiddenByState) {
    reasons.push(
      stateLabel ? (
        <Trans>Su estado operacional ({stateLabel}) no está entre los visibles para clientes.</Trans>
      ) : (
        <Trans>No visible para clientes por su estado operacional.</Trans>
      ),
    );
  }
  if (hiddenManually) {
    reasons.push(<Trans>Oculto manualmente para el cliente.</Trans>);
  }
  if (reasons.length === 0) {
    reasons.push(<Trans>El usuario tiene acceso, pero aún no verá este dispositivo.</Trans>);
  }

  return (
    <Tooltip
      title={
        <Box component="span" display="flex" flexDirection="column" gap={0.5}>
          {reasons.map((reason, index) => (
            <Box component="span" key={index}>
              {reason}
            </Box>
          ))}
        </Box>
      }
    >
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        icon={<VisibilityOff />}
        label={_(msg`Oculto para el cliente`)}
      />
    </Tooltip>
  );
};

export default ClientHiddenChip;
